import React, { useState, useCallback, useMemo } from 'react';

import {
  forEach, get, isEmpty, map,
} from 'lodash';
import {
  arrayOf, bool, func, shape, string,
} from 'prop-types';
import { QuestionsComponents } from '../../../../core/components/questions-components';
import ProgressBar from '../components/progress-bar';
import SubmitModal from '../../../components/modal-submit';
import { buildDisplayLogic } from '../helpers';

import {
  Button,
  Content,
  Segment,
  ContainerProgressBar,
  CenteredContainer,
} from './styles';
import { useErrorFields } from '../../../../core/hooks/use-errors-fields';

const SurveyQuestions = (props) => {
  const {
    isLastPage,
    loading,
    onSubmit,
    page = {},
    pagesList,
    surveyError,
    allQuestions,
    setAllQuestions,
    treatedAccumulatedAnswers,
  } = props;

  const [openModal, setOpenModal] = useState(false);
  const [hideBottomButton, setHideBottomButton] = useState(false);

  const buttonLabel = !isEmpty(surveyError) ? 'Retry' : 'Next';

  const { getErrorFields, findErrorField, removeErrorField } = useErrorFields(allQuestions);

  const handleOpenModal = () => {
    const errorFields = getErrorFields();

    if (errorFields.length > 0) {
      return false;
    }
    return setOpenModal(true);
  };

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const submitQuestions = useCallback(() => {
    const errorFields = getErrorFields();

    if (errorFields.length > 0) {
      return false;
    }
    handleCloseModal();
    return onSubmit(allQuestions);
  }, [getErrorFields, handleCloseModal, onSubmit, allQuestions]);

  const { skipLogic: { conditions = [] } = {} } = page;

  const isSkippingLogicToEndScreen = useMemo(() => {
    const hasConditions = conditions && conditions.length > 0;
    if (!hasConditions) return false;

    const skipLogicToEndScreenConditions = conditions.filter((condition) => condition.surveyPageId === 'end_screen');
    if (skipLogicToEndScreenConditions.length < 1) return false;

    const skipToEndScreenQuestionOptionIds = skipLogicToEndScreenConditions
      .flatMap((condition) => condition.skipConditions.map((skipCondition) => skipCondition.questionOptionId));

    const hasSelectedSkipToEndScreenOption = allQuestions.some((question) => {
      const { answer = {} } = question;
      const isQuestionObject = answer !== null && typeof answer === 'object';

      const questionOptionIds = isQuestionObject ? Object.keys(answer) : answer;

      if (isQuestionObject) {
        return skipToEndScreenQuestionOptionIds
          .some((skipToEndScreenQuestionOptionId) => questionOptionIds.includes(skipToEndScreenQuestionOptionId));
      }

      return skipToEndScreenQuestionOptionIds.includes(questionOptionIds);
    });

    return hasSelectedSkipToEndScreenOption;
  }, [conditions, allQuestions]);

  const onGoToNextPage = () => {
    if (isLastPage) {
      handleOpenModal();
    } else {
      submitQuestions();
    }
  };

  const cleanUpHiddenQuestions = (questionsList) => {
    let questionsChanged = false;

    // Extract answers from questionsList
    const currentPageAnswers = questionsList
      .filter((q) => q.answer !== undefined)
      .map((q) => {
        const { id, answer, questionType } = q;
        const answerResponse = questionType === 'range' ? answer : Object.keys(answer)[0];
        const answerResponseQuestionMultiple = questionType === 'multiple_select' ? { questionOptionIds: Object.keys(answer) }
          : { questionOptionId: answerResponse };

        return {
          questionId: id,
          ...answerResponseQuestionMultiple,
        };
      });

    // Combine accumulated answers with current page answers
    const allAnswersSoFar = treatedAccumulatedAnswers.length > 0
      ? [...treatedAccumulatedAnswers, ...currentPageAnswers]
      : [...currentPageAnswers];

    // Re-evaluate display logic
    const questionVisibility = buildDisplayLogic(allAnswersSoFar, pagesList);

    // Remove answers to hidden questions
    const hiddenQuestionIds = [];
    forEach(questionVisibility, (isVisible, questionId) => {
      if (!isVisible) {
        hiddenQuestionIds.push(questionId);
      }
    });

    const finalQuestions = questionsList.map((q) => {
      if (hiddenQuestionIds.includes(q.id)) {
        if (!isEmpty(q.answer)) {
          questionsChanged = true; // A question's answer was removed
        }
        return {
          ...q,
          answer: {},
          isVisible: false,
        };
      } return {
        ...q,
        isVisible: true,
      };
    });

    if (questionsChanged) {
      // If any questions were changed, recursively call the function
      return cleanUpHiddenQuestions(finalQuestions);
    }

    return finalQuestions;
  };

  const handleChange = (newValue, question, optionDescription) => {
    setAllQuestions((prevQuestions) => {
      const updatedQuestions = prevQuestions.map((thisQuestion) => {
        if (thisQuestion.id === question.id) {
          removeErrorField(question.id);
          if (optionDescription) {
            return { ...thisQuestion, answer: { newValue, optionDescription } };
          }
          return { ...thisQuestion, answer: newValue };
        }
        return thisQuestion;
      });

      // Start the recursive cleanup
      const finalQuestions = cleanUpHiddenQuestions(updatedQuestions);

      return finalQuestions;
    });
  };

  // hide the next button, redirect using submitQuestions function as prop
  const isFiveSecondPage = useMemo(() => page?.questions?.some((question) => question.questionType === 'five_second'), [page?.questions]);
  const isFirstClickTestPage = useMemo(
    () => page?.questions?.some((question) => question.questionType === 'first_click_test'),
    [page?.questions],
  );

  return (
    <Segment isfirstclicktestpage={+isFirstClickTestPage || 0}>
      <ContainerProgressBar>
        <ProgressBar
          pagesList={pagesList}
          page={page}
        />
      </ContainerProgressBar>

      {isEmpty(allQuestions) && !isEmpty(pagesList) && (<CenteredContainer>No questions</CenteredContainer>)}

      <Content>
        {map(allQuestions, (question, index) => (
          question.isVisible && (
            <QuestionsComponents
              key={question.id}
              errorField={findErrorField(question)}
              setHideBottomButton={setHideBottomButton}
              onChange={(value, optionDescription) => handleChange(value, question, optionDescription)}
              selectedValue={question.answer || ''}
              question={question}
              index={index}
              typeQuestion={get(question, 'questionType', '')}
              projectType="survey"
              submitQuestions={submitQuestions}
              onGoToNextPage={onGoToNextPage}
            />
          )
        ))}

        {!isFiveSecondPage && !hideBottomButton && (
          <Button
            data-tester-test-next
            onClick={isLastPage || isSkippingLogicToEndScreen ? handleOpenModal : submitQuestions}
          >
            {buttonLabel}
          </Button>
        )}

        {(openModal) && (
          <SubmitModal
            onClose={handleCloseModal}
            onSubmit={submitQuestions}
            show={openModal}
            loading={loading}
            error={surveyError}
          />
        )}
      </Content>
    </Segment>
  );
};

SurveyQuestions.propTypes = {
  page: shape({
    questions: arrayOf(shape()),
    id: string,
    displayQuestion: shape({
      conditions: arrayOf(shape()),
    }),
  }),
  isLastPage: bool,
  loading: bool,
  onSubmit: func,
  pagesList: arrayOf(shape()),
  surveyError: shape({}),
  allQuestions: arrayOf(shape({})),
  setAllQuestions: func,
  treatedAccumulatedAnswers: arrayOf(shape({})),
};

SurveyQuestions.defaultProps = {
  isLastPage: false,
  loading: false,
  onSubmit: () => { },
  page: {},
  pagesList: [],
  surveyError: {},
  allQuestions: [],
  setAllQuestions: () => { },
  treatedAccumulatedAnswers: [],
};

export default SurveyQuestions;
